/**
 * «Повторить заказ» — и вопрос, который он обязан задать.
 *
 * Повтор собирает черновик из прошлого заказа и кладёт его в то же единственное место, где живёт
 * незавершённая заявка (`saveOrderDraft` в конце `prepareRepeatDraft`). То есть повтор ВСЕГДА
 * затирает недооформленный заказ, если он был, — и раньше делал это молча: человек, набравший
 * адрес и комментарий, нажимал «Повторить» на соседней карточке и терял их без единого слова.
 *
 * Вопрос задаётся только когда есть что терять. Пустое хранилище — повтор идёт сразу: лишний
 * диалог там, где ничего не пропадает, учит нажимать «Да» не читая.
 *
 * Хук общий для двух мест, которые повтор предлагают, — кнопки на завершённом заказе и ссылки в
 * «Последних заказах»: правило одно, и разъехаться этим двум местам нельзя.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { loadOrderDraft } from '@/entities/order/draftStorage';
import { ConfirmSheet } from '@/shared/ui/ConfirmSheet';

export function useRepeatOrder(prepare: () => Promise<boolean>) {
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);

  const repeat = useMutation({
    mutationFn: prepare,
    onSuccess: (hasCar) => {
      // No car left: the picker first, because a wizard with nothing to fuel cannot advance past
      // its own first gate.
      void navigate({ to: hasCar ? '/order/new' : '/order/car' });
    },
  });

  function start() {
    if (loadOrderDraft() !== null) {
      setAsking(true);
      return;
    }
    repeat.mutate();
  }

  const confirmation = (
    <ConfirmSheet
      open={asking}
      title="Начать заказ заново?"
      subtitle="Незавершённая заявка будет удалена — введённые в ней данные не сохранятся."
      confirmLabel="Повторить заказ"
      onConfirm={() => {
        setAsking(false);
        repeat.mutate();
      }}
      onClose={() => setAsking(false)}
    />
  );

  return {
    start,
    /** Рисуется рядом с кнопкой — лист сам по себе ничего не занимает, пока закрыт. */
    confirmation,
    isPending: repeat.isPending,
    isError: repeat.isError,
    error: repeat.error,
  };
}
